import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { loginRequest } from '@/lib/auth/msalConfig';
import { extractIdToken } from '@/lib/auth/nativeImplicitAuth';
import { login } from '@/services/platform/data/authentication';
import { consumeDeepLinkTarget } from '@/lib/core/hooks/deepLinkTarget';

const AUTH_DEBUG = import.meta.env.VITE_AUTH_DEBUG === 'true';

export function AuthRedirectPage() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { setUser, setTokens, setHasPermissions } = useAuthStore();
  const [diagData, setDiagData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    async function handleRedirect() {
      const d: Record<string, unknown> = {
        ts: new Date().toISOString(),
        href: window.location.href,
        hash: window.location.hash,
        search: window.location.search,
      };

      try {
        let idToken: string | null = null;

        if (Capacitor.isNativePlatform()) {
          idToken = extractIdToken();
          d.idTokenFromHash = idToken ? '(present)' : null;
        } else {
          let response = await instance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
          d.responseNull = response === null;
          d.responseIdToken = response?.idToken ? '(present)' : null;

          if (!response?.idToken) {
            const accounts = instance.getAllAccounts();
            d.accounts = accounts.length;
            if (accounts.length > 0) {
              response = await instance.acquireTokenSilent({
                account: accounts[0],
                scopes: loginRequest.scopes,
              });
              d.silentIdToken = response?.idToken ? '(present)' : null;
            }
          }
          idToken = response?.idToken ?? null;
        }

        if (!idToken) {
          d.outcome = 'no_id_token';
          return;
        }

        const auth = await login(idToken);

        if (auth) {
          const { accessToken, refreshToken, expiresAt, user } = auth;

          if (accessToken && user) {
            setTokens(accessToken, refreshToken ?? null, expiresAt ?? null);
            setUser(user);
            setHasPermissions(false);
            d.outcome = 'success';
            navigate(consumeDeepLinkTarget() ?? '/', { replace: true });
          }
        }
      } catch (error) {
        d.error = String(error);
        console.error('[AuthRedirect] error', error);
      } finally {
        localStorage.setItem('auth_redirect_diag', JSON.stringify(d));
        console.log('[AuthRedirect] diag', JSON.stringify(d));
        setDiagData({ ...d });
        if (d.outcome !== 'success') {
          // Discard any remembered deep-link target — this login attempt failed, so it
          // must not be replayed against an unrelated later session.
          consumeDeepLinkTarget();
          navigate('/', { replace: true });
        }
      }
    }

    handleRedirect();
  }, [instance, navigate, setUser, setTokens, setHasPermissions]);

  if (!diagData) {
    return <p style={{ padding: 16, color: '#fff', background: '#000' }}>Authenticating…</p>;
  }

  if (AUTH_DEBUG) {
    return (
      <pre
        style={{
          padding: 16,
          fontSize: 11,
          background: '#000',
          color: '#0f0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          overflowY: 'auto',
          height: '100vh',
          margin: 0,
        }}
      >
        {JSON.stringify(diagData, null, 2)}
      </pre>
    );
  }

  return null;
}
